import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthImage from "./AuthImage";
import AuthVideo from "./AuthVideo";
import api, { API } from "../lib/api";
import { Avatar, AvatarFallback } from "./ui/avatar";
import MediaComments from "./MediaComments";
import useScreenshotDeter from "../hooks/useScreenshotDeter";
import BlackoutCurtain from "./BlackoutCurtain";
import ForensicOverlay from "./ForensicOverlay";
import PostThreadPanel from "./beta/PostThreadPanel";

/**
 * Modal som viser bilde/video i fullskjerm.
 *
 * Bruk:
 *  - Enkelt-modus: <MediaPlayerModal media={item} onClose={...} />
 *  - Galleri-modus: <MediaPlayerModal items={list} initialIndex={idx} onClose={...} />
 *
 * Funksjoner:
 *  - Brukernavn-link til profil (top-left)
 *  - Pil-navigering (venstre/høyre via knapper + tastatur)
 *  - Esc lukker
 */
export default function MediaPlayerModal({ media, items, initialIndex = 0, onClose }) {
  const { t } = useTranslation();
  // Modalen er bare åpen så lenge `media` er satt. `items` brukes som navigeringsliste.
  // Når foreldrekomponenten setter media=null, må modalen lukkes (return null).
  const isOpen = !!media;
  const list = items && items.length > 0 ? items : (media ? [media] : []);
  const [idx, setIdx] = useState(() => {
    if (items && media) {
      const found = items.findIndex((it) => it.id === media.id);
      return found >= 0 ? found : initialIndex;
    }
    return initialIndex;
  });

  // Reset idx når media/items endres
  useEffect(() => {
    if (items && media) {
      const found = items.findIndex((it) => it.id === media.id);
      if (found >= 0) setIdx(found);
    }
  }, [media, items]);

  const current = isOpen ? list[idx] : null;
  const hasPrev = isOpen && list.length > 1 && idx > 0;
  const hasNext = isOpen && list.length > 1 && idx < list.length - 1;
  const goPrev = () => hasPrev && setIdx((i) => i - 1);
  const goNext = () => hasNext && setIdx((i) => i + 1);

  // Like-state per bilde (laster på nytt ved bytte)
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeBusy, setLikeBusy] = useState(false);
  useEffect(() => {
    if (!current?.id) return;
    setLiked(false);
    setLikeCount(0);
    api.get(`/images/${current.id}/like-status`).then((r) => {
      setLiked(!!r.data?.liked);
      setLikeCount(r.data?.like_count || 0);
    }).catch(() => {});
  }, [current?.id]);
  const toggleLike = async (e) => {
    e?.stopPropagation();
    if (!current?.id || likeBusy) return;
    setLikeBusy(true);
    // optimistisk
    setLiked((v) => !v);
    setLikeCount((c) => liked ? Math.max(0, c - 1) : c + 1);
    try {
      const r = await api.post(`/images/${current.id}/like`);
      setLiked(!!r.data?.liked);
      setLikeCount(r.data?.like_count || 0);
    } catch {
      // revertere optimistisk
      setLiked((v) => !v);
      setLikeCount((c) => liked ? c + 1 : Math.max(0, c - 1));
    } finally {
      setLikeBusy(false);
    }
  };

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    // Skjul site-header når modalen er åpen så den ikke overlapper bildet
    document.body.style.overflow = "hidden";
    document.body.classList.add("lightbox-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.classList.remove("lightbox-open");
    };
    // eslint-disable-next-line
  }, [current, hasPrev, hasNext]);

  const isVideo = current?.media_type === "video";
  const owner = current?.owner;
  // Screenshot-deterrent — kun aktivt for bilder, ikke video (påvirker avspilling).
  const deter = useScreenshotDeter({ label: isVideo ? "gallery-video" : "gallery-image", enabled: !!current && !isVideo });

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-0 sm:p-4 backdrop-blur-sm overflow-hidden"
      onClick={onClose}
      data-testid="media-player-modal"
    >
      {/* Topbar: bruker + lukk */}
      <div className="absolute top-0 left-0 right-0 z-[110] flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 bg-gradient-to-b from-black/95 via-black/70 to-transparent"
           onClick={(e) => e.stopPropagation()}>
        {owner ? (
          <Link
            to={`/profile/${owner.username}`}
            className="flex items-center gap-3 group"
            data-testid="lightbox-owner-link"
          >
            <Avatar className="h-9 w-9 rounded-sm border border-white/20">
              {owner.avatar_url ? (
                <img src={owner.avatar_url} alt={owner.username}
                  className="h-full w-full object-cover" />
              ) : (
                <AvatarFallback className="bg-[var(--magenta)] text-black text-xs font-bold rounded-sm">
                  {(owner.username || "?").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <div className="font-fraunces italic text-lg text-[var(--cream)] group-hover:text-[var(--magenta)] transition-colors leading-tight">
                {owner.username}
              </div>
              {(owner.city || owner.age) && (
                <div className="font-meta text-[10px] text-[var(--cream-soft)]">
                  {owner.city}{owner.city && owner.age ? " · " : ""}{owner.age}
                </div>
              )}
            </div>
          </Link>
        ) : <span />}

        <div className="flex items-center gap-3">
          {list.length > 1 && (
            <span className="font-meta text-[10px] text-[var(--cream-soft)]" data-testid="lightbox-counter">
              {idx + 1} / {list.length}
            </span>
          )}
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full bg-white/10 hover:bg-[var(--magenta)] flex items-center justify-center transition-colors"
            data-testid="media-player-close"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Prev/Next-knapper */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-[var(--magenta)] flex items-center justify-center transition-colors"
          data-testid="lightbox-prev"
          aria-label="Forrige bilde"
        >
          <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/10 hover:bg-[var(--magenta)] flex items-center justify-center transition-colors"
          data-testid="lightbox-next"
          aria-label="Neste bilde"
        >
          <ChevronRight className="h-6 w-6 text-white" />
        </button>
      )}

      {/* Innhold */}
      <div
        className="relative w-full max-w-5xl h-full sm:h-auto flex flex-col lg:flex-row gap-0 bg-[var(--noir-elev)]"
        style={{ maxHeight: "100dvh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* VENSTRE: Media + caption */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div ref={deter.ref} className="relative bg-black flex-1 flex items-center justify-center overflow-hidden min-h-0">
            {isVideo ? (
              <AuthVideo
                videoId={current.id}
                controls
                autoPlay
                playsInline
                className="max-h-full max-w-full"
                data-testid="media-player-video"
              />
            ) : (
              <AuthImage
                key={current.id}
                imageId={current.id}
                alt={current.caption || ""}
                className="max-h-full max-w-full object-contain select-none"
                draggable={false}
              />
            )}
            {!isVideo && <ForensicOverlay viewId={current.id} tag={owner?.username} />}
            <BlackoutCurtain active={deter.blackout} testId="gallery-blackout" />
          </div>
          {/* Like + caption + tags */}
          <div className="border-t border-white/10 p-3 shrink-0 flex items-start gap-3">
            {!current.thread_post_id && <button
              type="button"
              onClick={toggleLike}
              disabled={likeBusy}
              data-testid="lightbox-like-btn"
              className="inline-flex items-center gap-1.5 shrink-0 group"
              aria-label={liked ? "Fjern like" : "Like"}
            >
              <Heart
                className={`h-6 w-6 transition-all ${liked
                  ? "fill-red-500 text-red-500"
                  : "text-white group-hover:text-red-500"}`}
                strokeWidth={liked ? 0 : 2}
              />
              <span className="font-manrope text-sm text-[var(--cream-soft)]" data-testid="lightbox-like-count">
                {likeCount}
              </span>
            </button>}
            <div className="flex-1 min-w-0">
              {current.caption && (
                <p className="font-fraunces italic text-base text-[var(--cream)] leading-snug">
                  {current.caption}
                </p>
              )}
              {current.tags?.length > 0 && (
                <div className="font-meta mt-1">
                  {current.tags.map((t) => "#" + t).join(" ")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* HØYRE: Kommentar-panel med egen scroll */}
        <div
          className="lg:w-[320px] lg:shrink-0 max-h-[40vh] lg:max-h-none border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col overflow-hidden"
          data-testid="lightbox-comments-pane"
        >
          {current.thread_post_id ? (
            <PostThreadPanel postId={current.thread_post_id} fromPost={!!current.source_post_id} />
          ) : (
            <MediaComments
              targetType="image"
              targetId={current.id}
              ownerId={current.owner?.id || current.owner_id}
              compact
            />
          )}
        </div>
      </div>
    </div>
  );
}
